/**
 * Website rich text (ADR 0081): the small Markdown subset staff write in HO admin and the website shows. One definition for both:
 * `parseRichText` reads it into a tree the website renders with its own components (never HTML, rule 15) and HO admin's editor
 * loads; `toRichText` writes a tree back. What is allowed:
 *
 * - headings `#`, `##`, `###` (shown as h2–h4: the page title is the h1), paragraphs (lines join; a blank line ends one);
 * - `**bold**`, `_italic_` (or `*italic*`), `[text](link)`; a backslash makes the next mark plain (`\*`);
 * - lists `- item` and `1. item`; quotes `> text`; a divider `---`;
 * - a picture on its own line `![alt text](address)`;
 * - a table: `| a | b |`, then `| --- | --- |`, then its rows (the first row is the header).
 *
 * Links must be a site path or https; pictures also an address in the file store (`ownFiles`). Anything else is plain text, and
 * a picture without a safe address or alt text is left out.
 *
 * An email template (ADR 0125) is the same subset read with `links: 'email'`: links may be https, http, mailto or tel, an address
 * with `{value}` placeholders in it, or a lone placeholder (`{tracking_url}`); pictures must be https and may have no alt text. The
 * server reads it the same way (EmailMarkdown.cs).
 */

/** Which links and pictures the text may have: the website's (default) or an email's (ADR 0125). */
export type LinkMode = 'site' | 'email';

export type Inline = { kind: 'text'; text: string } | { kind: 'bold' | 'italic'; children: Inline[] } | { kind: 'link'; href: string; children: Inline[] };

export type RichBlock =
  | { kind: 'heading'; level: 2 | 3 | 4; children: Inline[] }
  | { kind: 'paragraph'; children: Inline[] }
  | { kind: 'list'; ordered: boolean; items: Inline[][] }
  | { kind: 'quote'; paragraphs: Inline[][] }
  | { kind: 'divider' }
  | { kind: 'image'; src: string; alt: string }
  | { kind: 'table'; header: Inline[][]; rows: Inline[][][] };

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

/** A link the site may follow: a path on the site, or an https address. Anything else (script, other schemes) is dropped. */
export function safeHref(v: unknown): string | null {
  const href = str(v);
  if (!href) return null;
  if (href.startsWith('/') && !href.startsWith('//') && !href.includes('\\')) return href;
  try {
    return new URL(href).protocol === 'https:' ? href : null;
  } catch {
    return null;
  }
}

const LONE_PLACEHOLDER = /^\{[a-z0-9_]+\}$/;

/**
 * A link an email template may have (ADR 0125): a lone placeholder (`{tracking_url}`), or an https, http, mailto or tel address,
 * placeholders allowed in it (`https://x.vn/a?no={account_no}`). Anything else is dropped. The value is checked again once filled.
 */
export function emailHref(v: unknown): string | null {
  const href = str(v);
  if (!href) return null;
  if (LONE_PLACEHOLDER.test(href)) return href;
  try {
    return ['https:', 'http:', 'mailto:', 'tel:'].includes(new URL(href.replace(/[{}]/g, '')).protocol) ? href : null;
  } catch {
    return null;
  }
}

/** A picture an email may show: an https address. */
export function emailImage(v: unknown): string | null {
  const href = str(v);
  if (!href) return null;
  try {
    return new URL(href).protocol === 'https:' ? href : null;
  } catch {
    return null;
  }
}

/**
 * A picture the site may show: what {@link safeHref} keeps, or one uploaded to HO's file store at its own address (`ownFiles`,
 * http in development), as HO accepts it.
 */
export function pictureHref(v: unknown, ownFiles?: string | null): string | null {
  const href = str(v);
  if (href && ownFiles && href.startsWith(`${ownFiles.replace(/\/+$/, '')}/`) && !href.includes('..') && !href.includes('\\')) return href;
  return safeHref(v);
}

// Escaped characters become private-use code points while parsing, so no rule sees them, and come back as themselves.
const ESCAPABLE = /\\([\\`*_{}[\]()#+\-.!|>~])/g;
const PRIVATE = 0xe000;
const hide = (s: string) => s.replace(ESCAPABLE, (_, c: string) => String.fromCharCode(PRIVATE + c.charCodeAt(0)));
const show = (s: string) => s.replace(/[-]/g, (c) => String.fromCharCode(c.charCodeAt(0) - PRIVATE));

const HEADING = /^(#{1,3})\s+(.+)$/;
const BULLET = /^[-*+]\s+(.+)$/;
const NUMBERED = /^\d+[.)]\s+(.+)$/;
const QUOTE = /^>\s?(.*)$/;
const DIVIDER = /^(-{3,}|\*{3,}|_{3,})$/;
const IMAGE = /^!\[([^\]]*)\]\(([^)\s]+)\)$/;
const TABLE_RULE = /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?$/;

const cells = (line: string, links: LinkMode) =>
  line
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => parseInlineHidden(c.trim(), links));

/**
 * @param options.ownFiles the file store's address: pictures there are kept even over http (development).
 * @param options.editing HO admin's editor: keep any http(s) or site-path picture as written, since the editor doesn't know the file
 *   store's address; the server checks every address when the draft is saved.
 * @param options.links `email` for an email template's links and pictures (ADR 0125); the website's by default.
 */
export function parseRichText(source: string, options: { ownFiles?: string | null; editing?: boolean; links?: LinkMode } = {}): RichBlock[] {
  const links = options.links ?? 'site';
  const parseHidden = (text: string) => parseInlineHidden(text, links);
  const blocks: RichBlock[] = [];
  const lines = hide(source.replace(/\r\n?/g, '\n')).split('\n').map((l) => l.trim());
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: Inline[][] } | null = null;
  let quote: string[][] | null = null;
  const flush = () => {
    if (paragraph.length) blocks.push({ kind: 'paragraph', children: parseHidden(paragraph.join(' ')) });
    paragraph = [];
    if (list) blocks.push({ kind: 'list', ...list });
    list = null;
    if (quote) {
      const paragraphs = quote.filter((p) => p.length).map((p) => parseHidden(p.join(' ')));
      if (paragraphs.length) blocks.push({ kind: 'quote', paragraphs });
    }
    quote = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';
    if (!line) {
      flush();
      continue;
    }

    const quoted = QUOTE.exec(line);
    if (quoted) {
      if (!quote) flush();
      quote ??= [[]];
      const text = quoted[1]?.trim() ?? '';
      if (text) quote[quote.length - 1]?.push(text);
      else quote.push([]);
      continue;
    }

    if (DIVIDER.test(line)) {
      flush();
      blocks.push({ kind: 'divider' });
      continue;
    }

    const image = IMAGE.exec(line);
    if (image) {
      flush();
      const alt = show(image[1] ?? '').trim();
      const address = show(image[2] ?? '');
      if (links === 'email') {
        // An email's picture may go without alt text (the server's rule); its address must be https.
        const src = emailImage(address);
        if (src) blocks.push({ kind: 'image', src, alt });
        continue;
      }

      const src = options.editing && /^http:\/\/[^/]/i.test(address) ? address : pictureHref(address, options.ownFiles);
      if (src && alt) blocks.push({ kind: 'image', src, alt });
      continue;
    }

    if (line.startsWith('|') && TABLE_RULE.test(lines[i + 1] ?? '')) {
      flush();
      const header = cells(line, links);
      const rows: Inline[][][] = [];
      i += 2;
      for (; i < lines.length && (lines[i] ?? '').startsWith('|'); i++) {
        const row = cells(lines[i] ?? '', links);
        rows.push(header.map((_, k) => row[k] ?? []));
      }

      i--;
      blocks.push({ kind: 'table', header, rows });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      blocks.push({ kind: 'heading', level: ((heading[1]?.length ?? 1) + 1) as 2 | 3 | 4, children: parseHidden(heading[2] ?? '') });
      continue;
    }

    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    if (bullet || numbered) {
      const ordered = !!numbered;
      if (paragraph.length || quote || (list && list.ordered !== ordered)) flush();
      list ??= { ordered, items: [] };
      list.items.push(parseHidden((bullet ?? numbered)?.[1] ?? ''));
      continue;
    }

    if (list || quote) flush();
    paragraph.push(line);
  }

  flush();
  return blocks;
}

/** **bold**, _italic_ or *italic*, [text](href); anything unmatched stays text. */
export function parseInline(text: string, options: { links?: LinkMode } = {}): Inline[] {
  return parseInlineHidden(hide(text), options.links ?? 'site');
}

function parseInlineHidden(text: string, links: LinkMode): Inline[] {
  const parseHidden = (t: string) => parseInlineHidden(t, links);
  const out: Inline[] = [];
  let rest = text;
  const push = (t: string) => {
    if (!t) return;
    const last = out[out.length - 1];
    if (last?.kind === 'text') last.text += show(t);
    else out.push({ kind: 'text', text: show(t) });
  };

  while (rest) {
    const match = /\*\*(.+?)\*\*|\*(.+?)\*|(?<![\p{L}\p{N}])_(.+?)_(?![\p{L}\p{N}])|\[([^\]]+)\]\(([^)\s]+)\)/u.exec(rest);
    if (!match) {
      push(rest);
      break;
    }

    push(rest.slice(0, match.index));
    if (match[1] !== undefined) out.push({ kind: 'bold', children: parseHidden(match[1]) });
    else if (match[2] !== undefined || match[3] !== undefined) out.push({ kind: 'italic', children: parseHidden(match[2] ?? match[3] ?? '') });
    else {
      const href = links === 'email' ? emailHref(show(match[5] ?? '')) : safeHref(show(match[5] ?? ''));
      const label = match[4] ?? '';
      if (href) out.push({ kind: 'link', href, children: parseHidden(label) });
      else push(label);
    }

    rest = rest.slice(match.index + match[0].length);
  }

  return out;
}

// ---------------------------------------------------------------------------------------------- writing

const escapeMarks = (s: string, inTable: boolean) => s.replace(inTable ? /[\\*_[\]|]/g : /[\\*_[\]]/g, (c) => `\\${c}`);

/** Text as Markdown; in an email (ADR 0125) a `{value}` is written as it is, so `{expiry_date}` stays a value. */
const escapeText = (s: string, inTable: boolean, links: LinkMode = 'site') => {
  const text = s.replace(/\s+/g, ' ');
  if (links !== 'email') return escapeMarks(text, inTable);
  return text
    .split(/(\{[a-z0-9_]+\})/)
    .map((part, i) => (i % 2 ? part : escapeMarks(part, inTable)))
    .join('');
};

const ADDRESS: Record<string, string> = { '(': '%28', ')': '%29' };
const escapeAddress = (s: string) => s.replace(/[\s()]/g, (c) => ADDRESS[c] ?? encodeURIComponent(c));

const WORD = /[\p{L}\p{N}]/u;

function writeInline(items: Inline[], inTable = false, links: LinkMode = 'site'): string {
  const parts = items.map((x): string | { italic: string } => {
    switch (x.kind) {
      case 'text':
        return escapeText(x.text, inTable, links);
      case 'bold': {
        const inner = writeInline(x.children, inTable, links);
        return inner.trim() ? `**${inner}**` : inner;
      }
      case 'italic': {
        const inner = writeInline(x.children, inTable, links);
        return inner.trim() ? { italic: inner } : inner;
      }
      case 'link':
        return `[${writeInline(x.children, inTable, links) || escapeText(x.href, inTable, links)}](${escapeAddress(x.href)})`;
    }
  });
  // Italic is _x_, which never splits a word; inside a word (next to a letter or digit) it is *x*.
  return parts
    .map((p, i) => {
      if (typeof p === 'string') return p;
      const before = parts[i - 1];
      const after = parts[i + 1];
      const touches =
        (typeof before === 'string' && WORD.test(before.slice(-1))) || (typeof after === 'string' && WORD.test(after.slice(0, 1)));
      return touches ? `*${p.italic}*` : `_${p.italic}_`;
    })
    .join('');
}

/** A line's start that would read as a heading, list, quote, table, picture or divider is written escaped. */
const guardStart = (line: string) =>
  line
    .replace(/^([#>|!+-])/, '\\$1')
    .replace(/^(\d+)([.)])(\s)/, '$1\\$2$3');

/**
 * The Markdown for a tree: `parseRichText(toRichText(blocks))` gives the same tree back.
 * @param options.links `email`: a picture without alt text is kept, as an email may have one (ADR 0125).
 */
export function toRichText(blocks: RichBlock[], options: { links?: LinkMode } = {}): string {
  const mode = options.links ?? 'site';
  const inlineText = (items: Inline[], inTable = false) => writeInline(items, inTable, mode);
  const out: string[] = [];
  for (const b of blocks) {
    switch (b.kind) {
      case 'heading': {
        const text = inlineText(b.children).trim();
        if (text) out.push(`${'#'.repeat(b.level - 1)} ${text}`);
        break;
      }
      case 'paragraph': {
        const text = inlineText(b.children).trim();
        if (text) out.push(guardStart(text));
        break;
      }
      case 'list': {
        const items = b.items.map((i) => inlineText(i).trim()).filter(Boolean);
        if (items.length) out.push(items.map((t, k) => (b.ordered ? `${k + 1}. ${t}` : `- ${t}`)).join('\n'));
        break;
      }
      case 'quote': {
        const paragraphs = b.paragraphs.map((p) => inlineText(p).trim()).filter(Boolean);
        if (paragraphs.length) out.push(paragraphs.map((p) => `> ${p}`).join('\n>\n'));
        break;
      }
      case 'divider':
        out.push('---');
        break;
      case 'image':
        if ((b.alt.trim() || mode === 'email') && b.src) out.push(`![${escapeText(b.alt.trim(), false, mode)}](${escapeAddress(b.src)})`);
        break;
      case 'table': {
        const width = Math.max(b.header.length, ...b.rows.map((r) => r.length));
        if (!width) break;
        const row = (cs: Inline[][]) =>
          `| ${Array.from({ length: width }, (_, k) => inlineText(cs[k] ?? [], true).trim() || ' ').join(' | ')} |`;
        out.push([row(b.header), `| ${Array.from({ length: width }, () => '---').join(' | ')} |`, ...b.rows.map(row)].join('\n'));
        break;
      }
    }
  }

  return out.join('\n\n');
}
